/* Copyright (c) 2020-present CloudIO Inc */

import { bingMapNavigatorTravelMode } from 'componentregistry/registry/constants';
import { hasAutoLayoutParent } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { defaultValue } from './BingMapNavigator';
import { page as SamplePage } from '../../help/sample_pages/BingMapNavigatorPage';
import { isEmpty } from '../../utils/isEmpty';

const BingMapNavigatorManager: ComponentManagerFn<'BingMapNavigator'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'BingMapNavigator'>(platform, 'BingMapNavigator'),
    type: 'BingMapNavigator',
    name: 'Bing Map Navigator',
    defaultValue,
    props: {
      travelMode: {
        label: 'Travel Mode',
        type: 'select',
        options: bingMapNavigatorTravelMode,
        helpText: `Driving(Default): indicates standard driving directions using the road network.
                   Walking: requests walking directions via pedestrian paths & sidewalks.
                   Transit: requests directions via public transit routes.`,
      },
      navigatorHeight: {
        label: 'Height',
        type: 'number',
        min: 0,
        max: 800,
        step: 40,
      },
      showDashboard: {
        label: 'Show Dashboard',
        type: 'boolean',
      },
      showMapTypeSelector: {
        label: 'Show Map Type Selector',
        type: 'boolean',
      },
      showLocateMe: {
        label: 'Show Locate Me',
        type: 'boolean',
      },
      showZoomButtons: {
        label: 'Show Zoom Buttons',
        type: 'boolean',
      },
      startLatAttribute: {
        label: 'Start Latitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startLongAttribute: {
        label: 'Start Longitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endLatAttribute: {
        label: 'End Latitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endLongAttribute: {
        label: 'End Longitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startMarkerIcon: {
        label: 'Start Marker Icon',
        type: 'iconselect',
        category: 'Data',
      },
      endMarkerIcon: {
        label: 'End Marker Icon',
        type: 'iconselect',
        category: 'Data',
      },
      startMarkerIconAttribute: {
        label: 'Start Marker Icon Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endMarkerIconAttribute: {
        label: 'End Marker Icon Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startMarkerTitleAttribute: {
        label: 'Start Marker Title',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endMarkerTitleAttribute: {
        label: 'End Marker Title',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startMarkerIconColor: {
        label: 'Start Marker Icon Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Data',
      },
      endMarkerIconColor: {
        label: 'End Marker Icon Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Data',
      },
      routePathColor: {
        label: 'Route Path Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Data',
      },
      markerSize: {
        label: 'Marker Size',
        type: 'numberInput',
        category: 'Data',
      },
    },
    isPropertyVisible: (property, itemData, context) => {
      switch (property) {
        case 'navigatorHeight': {
          const page = platform.getPage(context.appUid, context.pageId);
          return hasAutoLayoutParent(page, page[context.itemId]).result;
        }
        case 'startMarkerIconAttribute':
          return isEmpty(itemData.startMarkerIcon.iconName);
        case 'endMarkerIconAttribute':
          return isEmpty(itemData.endMarkerIcon.iconName);
        case 'startMarkerIcon':
          return isEmpty(itemData.startMarkerIconAttribute);
        case 'endMarkerIcon':
          return isEmpty(itemData.endMarkerIconAttribute);
        default:
          return true;
      }
    },
    getDisplayText: (item) => '',
    category: 'Maps',
    icon: { iconName: 'map-marked-alt' },
    description:
      'Use this component to track object current position to destination using bing map.',
    helpRenders: [
      {
        title: 'Bing Map Naviagtor with all the properties',
        description:
          'Specify these properties to change the map/marker configuration',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 350,
        visibleProps: [
          'travelMode',
          'showDashboard',
          'showMapTypeSelector',
          'showLocateMe',
          'showZoomButtons',
          'startMarkerIcon',
          'endMarkerIcon',
          'markerSize',
          'startMarkerIconColor',
          'endMarkerIconColor',
          'routePathColor',
        ],
        item: {
          children: [],
          itemId: 'bingMapNavigator',
          parent: 'flexItem',
          props: {
            startLatAttribute: 'startLat',
            startLongAttribute: 'startLong',
            endLatAttribute: 'endLat',
            endLongAttribute: 'endLong',
            startMarkerTitleAttribute: 'startMarkerTitle',
            endMarkerTitleAttribute: 'endMarkerTitle',
            startMarkerIconColor: { type: 'text', value: 'orange' },
            endMarkerIconColor: { type: 'text', value: 'green' },
            routePathColor: { type: 'text', value: 'red' },
            markerSize: 40,
            travelMode: 'walking',
          },
          type: 'BingMapNavigator',
        },
      },
    ],
  };
};

export default BingMapNavigatorManager;
